import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import Input from '../../../../components/ui/Input';
import Button from '../../../../components/ui/Button';
import EmailValidator from './EmailValidator';
import PasswordStrengthMeter from './PasswordStrengthMeter';
import NameFormatHelper from './NameFormatHelper';

const RegistrationForm = ({ initialData, onSubmit, countryCode }) => {
    const { t } = useTranslation('common');
    const [formData, setFormData] = useState({
        fullName: initialData?.fullName || '',
        email: initialData?.email || '',
        password: initialData?.password || '',
        confirmPassword: initialData?.confirmPassword || '',
        termsAccepted: false
    });

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [emailValidation, setEmailValidation] = useState({ isValid: false, message: '' });
    const [passwordStrength, setPasswordStrength] = useState({ score: 0, feedback: [] });

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        // Full name validation
        if (!formData.fullName.trim()) {
            newErrors.fullName = 'Full name is required';
        } else if (formData.fullName.length < 2) {
            newErrors.fullName = 'Full name must be at least 2 characters';
        } else if (!formData.fullName.includes(' ')) {
            newErrors.fullName = 'Please enter both first and last name';
        }

        // Email validation
        if (!emailValidation.isValid) {
            newErrors.email = emailValidation.message || 'Please enter a valid email address';
        }

        // Password validation
        if (!formData.password) {
            newErrors.password = 'Password is required';
        } else if (passwordStrength.score < 3) {
            newErrors.password = 'Please choose a stronger password';
        }

        // Confirm password validation
        if (!formData.confirmPassword) {
            newErrors.confirmPassword = 'Please confirm your password';
        } else if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }

        // Terms acceptance validation
        if (!formData.termsAccepted) {
            newErrors.termsAccepted = 'You must accept the Terms of Service and Privacy Policy';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);
        try {
            // Parse full name into first and last name
            const nameParts = formData.fullName.trim().split(' ');
            const firstName = nameParts[0];
            const lastName = nameParts.slice(1).join(' ');

            await onSubmit({
                firstName: firstName,
                lastName: lastName,
                email: formData.email.toLowerCase().trim(),
                password: formData.password,
                termsAccepted: formData.termsAccepted
            });
        } catch (error) {
            console.error('Registration error:', error);
            setErrors({ submit: 'Registration failed. Please try again.' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const isFormValid = emailValidation.isValid &&
        passwordStrength.score >= 3 &&
        formData.fullName.length >= 2 &&
        formData.fullName.includes(' ') &&
        formData.password === formData.confirmPassword &&
        formData.termsAccepted;

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            {/* Full Name Field */}
            <div>
                <Input
                    label="Full Name"
                    value={formData.fullName}
                    onChange={(e) => handleInputChange('fullName', e.target.value)}
                    error={errors.fullName}
                    placeholder="Enter your full name"
                    autoComplete="name"
                    required
                />
            </div>

            {/* Name Format Helper */}
            <NameFormatHelper
                firstName={formData.fullName.split(' ')[0] || ''}
                lastName={formData.fullName.split(' ').slice(1).join(' ') || ''}
                countryCode={countryCode}
            />

            {/* Email Field */}
            <div>
                <Input
                    type="email"
                    label="Email Address"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    error={errors.email}
                    placeholder="Enter your email address"
                    autoComplete="email"
                    required
                />
                <EmailValidator
                    email={formData.email}
                    onValidationChange={setEmailValidation}
                />
            </div>

            {/* Password Field */}
            <div>
                <Input
                    type="password"
                    label="Password"
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    error={errors.password}
                    placeholder="Create a strong password"
                    autoComplete="new-password"
                    required
                />
                <PasswordStrengthMeter
                    password={formData.password}
                    onStrengthChange={setPasswordStrength}
                />
            </div>

            {/* Confirm Password Field */}
            <div>
                <Input
                    type="password"
                    label="Confirm Password"
                    value={formData.confirmPassword}
                    onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                    error={errors.confirmPassword}
                    placeholder="Confirm your password"
                    autoComplete="new-password"
                    required
                />
            </div>

            {/* Terms and Privacy Agreement */}
            <div className="space-y-2">
                <label className="flex items-start space-x-3">
                    <input
                        type="checkbox"
                        checked={formData.termsAccepted}
                        onChange={(e) => handleInputChange('termsAccepted', e.target.checked)}
                        className="mt-1 w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <span className="text-sm text-accent-700">
                        I agree to the{' '}
                        <a href="/terms" className="text-primary-600 hover:underline" target="_blank" rel="noopener noreferrer">
                            Terms of Service
                        </a>{' '}
                        and{' '}
                        <a href="/privacy" className="text-primary-600 hover:underline" target="_blank" rel="noopener noreferrer">
                            Privacy Policy
                        </a>
                    </span>
                </label>
                {errors.termsAccepted && (
                    <div className="text-red-600 text-sm">
                        {errors.termsAccepted}
                    </div>
                )}
            </div>

            {/* Submit Error */}
            {errors.submit && (
                <div className="text-red-600 text-sm text-center">
                    {errors.submit}
                </div>
            )}

            {/* Submit Button */}
            <Button
                type="submit"
                disabled={!isFormValid || isSubmitting}
                fullWidth
                variant="primary"
                size="lg"
                loading={isSubmitting}
            >
                {isSubmitting ? 'Creating Account...' : 'Create Account'}
            </Button>
        </form>
    );
};

export default RegistrationForm; 